// frontend/components/settings/GeneralSettingsTab.tsx

"use client";

import { Monitor } from "lucide-react";
import UserNotifySection from "./UserNotifySection";

interface GeneralSettingsTabProps {
  theme: string | undefined;
  colorTheme: string;
  handleThemeChange: (mode: string, color: string) => void;
  session?: any;
  accessToken?: string;
}

const LIGHT_THEMES = [
  { color: "blue",     bgClass: "bg-[#006DBA]",                              ringClass: "ring-[#006DBA]/40" },
  { color: "red",      bgClass: "bg-[#D32F2F]",                              ringClass: "ring-[#D32F2F]/40" },
  { color: "green",    bgClass: "bg-[#2E7D32]",                              ringClass: "ring-[#2E7D32]/40" },
  { color: "neutral",  bgClass: "bg-gray-200 border border-gray-300",        ringClass: "ring-gray-400/40" },
  { color: "lavender", bgClass: "bg-violet-300",                             ringClass: "ring-violet-400/40" },
  { color: "rose",     bgClass: "bg-rose-300",                               ringClass: "ring-rose-400/40" },
  { color: "mint",     bgClass: "bg-teal-300",                               ringClass: "ring-teal-400/40" },
  { color: "amber",    bgClass: "bg-amber-300",                              ringClass: "ring-amber-400/40" },
];

const DARK_THEMES = [
  { color: "blue",     bgClass: "bg-[#002D47] border border-zinc-600",       ringClass: "ring-[#004B7A]/60" },
  { color: "red",      bgClass: "bg-[#380909] border border-zinc-600",       ringClass: "ring-[#5D1515]/60" },
  { color: "green",    bgClass: "bg-[#09240D] border border-zinc-600",       ringClass: "ring-[#164A1F]/60" },
  { color: "neutral",  bgClass: "bg-[#111111] border border-zinc-600",       ringClass: "ring-zinc-500/60" },
  { color: "lavender", bgClass: "bg-[#1E1B4B] border border-zinc-600",       ringClass: "ring-violet-500/60" },
  { color: "rose",     bgClass: "bg-[#4C0519] border border-zinc-600",       ringClass: "ring-rose-500/60" },
  { color: "mint",     bgClass: "bg-[#042F2E] border border-zinc-600",       ringClass: "ring-teal-500/60" },
  { color: "amber",    bgClass: "bg-[#451A03] border border-zinc-600",       ringClass: "ring-amber-500/60" },
];

export default function GeneralSettingsTab({ theme, colorTheme, handleThemeChange, session, accessToken }: GeneralSettingsTabProps) {
  return (
    <div className="space-y-5">
      {/* 라이트 모드 */}
      <div>
        <p className="text-xs font-semibold text-gray-400 dark:text-zinc-500 mb-2.5 tracking-wide">라이트 모드</p>
        <div className="flex items-center flex-wrap gap-2">
          {LIGHT_THEMES.map(t => (
            <button
              key={`light-${t.color}`}
              title={t.color}
              onClick={() => handleThemeChange("light", t.color)}
              className={`w-7 h-7 rounded-full shadow transition-transform hover:scale-110 ${t.bgClass} ${
                theme === "light" && colorTheme === t.color ? `ring-4 ${t.ringClass} ring-offset-2 dark:ring-offset-zinc-900` : ""
              }`}
            />
          ))}
        </div>
      </div>

      {/* 다크 모드 */}
      <div>
        <p className="text-xs font-semibold text-gray-400 dark:text-zinc-500 mb-2.5 tracking-wide">다크 모드</p>
        <div className="flex items-center flex-wrap gap-2">
          {DARK_THEMES.map(t => (
            <button
              key={`dark-${t.color}`}
              title={t.color}
              onClick={() => handleThemeChange("dark", t.color)}
              className={`w-7 h-7 rounded-full shadow transition-transform hover:scale-110 ${t.bgClass} ${
                theme === "dark" && colorTheme === t.color ? `ring-4 ${t.ringClass} ring-offset-2 dark:ring-offset-zinc-900` : ""
              }`}
            />
          ))}
        </div>
      </div>

      {/* 시스템 설정 */}
      <div className="pt-1 border-t border-gray-100 dark:border-zinc-800">
        <button
          onClick={() => handleThemeChange("system", colorTheme)}
          className={`w-full flex items-center justify-center gap-2 p-2.5 rounded-lg border text-sm font-medium transition-all ${
            theme === "system"
              ? "border-gray-700 bg-gray-100 text-gray-900 dark:border-zinc-500 dark:bg-zinc-800 dark:text-zinc-100"
              : "border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-800 text-gray-500 dark:text-zinc-400"
          }`}
        >
          <Monitor size={14} />
          <span>시스템 설정에 따름</span>
        </button>
      </div>

      {session?.user?.role !== "ADMIN" && (
        <UserNotifySection accessToken={accessToken} />
      )}
    </div>
  );
}
